/* /industries (and "/ae/industries"…) — public index of the industry packs with a side-by-side comparison, for the
 * page's GCC country. */
import { Link } from "react-router-dom";
import { INDUSTRIES } from "../../../domain/industry";
import { useI18n } from "../../../i18n";
import { Icon } from "../../../ui";
import { MktFooter, MktHeader } from "../components/MktChrome";
import { MktLink } from "../components/MktLink";
import { useSeo } from "../../../seo/useSeo";
import "../home-extra.css";
import { IndustryCards } from "./IndustryCards";
import { inCountry, signupRoute, useMktCountry } from "../mktCountry";
import { inName, industriesIn, isSaudi, localise, MKT_SCOPE } from "../mktLocal";
import { CountryScope } from "../../../lib/country";
import "./industries.css";

/** The page under the marketing scope (see MKT_SCOPE): its copy is worded for the page's country by mktLocal. */
export default function Industries() {
  return <CountryScope country={MKT_SCOPE}><IndustriesPage /></CountryScope>;
}

function IndustriesPage() {
  const { L } = useI18n();
  const cc = useMktCountry();
  const sa = isSaudi(cc);
  useSeo(inCountry("/industries", cc));
  const packs = industriesIn(cc);
  const liveOf = (k: string) => packs.find((p) => p.key === k)!.features.filter((f) => f.status === "live").length;
  const total = INDUSTRIES.reduce((n, c) => n + c.features.length, 0);
  const live = INDUSTRIES.reduce((n, c) => n + liveOf(c.key), 0);
  const the = inName(cc);
  return (
    <div className="mkt" data-testid="industries">
      <MktHeader links={[]} onHome={false} />
      <section className="hero2 ip-hero">
        <div className="sadu" aria-hidden="true" />
        <div className="mkt-in">
          <div className="hero2-t">
            <span className="eyebrow">{L("Industry packs", "الحزم القطاعية")}</span>
            <h1>{sa ? L("One HR system, shaped to your industry", "نظام موارد بشرية واحد بمقاس قطاعك") : L(`One HR system, shaped to your industry in ${the[0]}`, `نظام موارد بشرية واحد بمقاس قطاعك في ${the[1]}`)}</h1>
            <p>{L(localise("Pick your business category at sign-up. StartHR switches on the certificates, Saudization quotas, shifts, site costing and pay elements your sector needs, on top of payroll, GOSI, WPS and every core module.", cc, "en"), localise("اختر نشاطك عند التسجيل، ويفعّل ستارت HR الشهادات ونسب التوطين والورديات وتكلفة المواقع وعناصر الأجر التي يحتاجها قطاعك، إضافةً إلى الرواتب والتأمينات وحماية الأجور وكل الوحدات الأساسية.", cc, "ar"))}</p>
            <span className="xs" style={{ color: "#a9c4ba" }} data-testid="ind-totals">{L(`${INDUSTRIES.length} industries · ${total} specialised features · ${live} live today`, `${INDUSTRIES.length} قطاعًا · ${total} ميزة متخصصة · ${live} متاحة اليوم`)}</span>
          </div>
        </div>
      </section>
      <section className="mkt-sec">
        <div className="mkt-in"><IndustryCards /></div>
      </section>
      <section className="mkt-sec alt">
        <div className="mkt-in">
          <div className="sec-h"><span className="eyebrow">{L("Compare", "قارن")}</span><h2>{L("What each pack adds", "ما تضيفه كل حزمة")}</h2></div>
          <div className="tw card">
            <table className="tbl ip-cmp" data-testid="ind-compare">
              <thead><tr><th>{L("Industry", "القطاع")}</th><th className="end">{L("Live features", "ميزات متاحة")}</th><th className="end">{L("Roadmap", "خطة التطوير")}</th><th className="end">{L("Certificates tracked", "شهادات متابعة")}</th>{sa && <th className="end">{L("Quota rules", "قواعد التوطين")}</th>}<th>{L("Summer midday ban", "حظر الظهيرة")}</th><th>{L("Sites module", "وحدة المواقع")}</th></tr></thead>
              <tbody>
                {INDUSTRIES.map((c) => (
                  <tr key={c.key} data-testid={"cmp-" + c.key}>
                    <td><MktLink to={inCountry("/industries/" + c.key, cc)}>{L(...c.name)}</MktLink></td>
                    <td className="end num">{liveOf(c.key)}</td>
                    <td className="end num">{c.features.length - liveOf(c.key)}</td>
                    <td className="end num">{c.certs.length}</td>
                    {sa && <td className="end num">{c.quotas.length}</td>}
                    <td>{c.heat ? <span className="chip warn">{L("Checked", "يُتحقق منه")}</span> : <span className="muted">—</span>}</td>
                    <td>{L(...c.sites.label)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ justifyContent: "center", marginTop: 24 }}>
            <Link className="btn gold lg" to={signupRoute(cc)} data-testid="ind-trial"><span>{L("Start your 30-day free trial", "ابدأ تجربة 30 يومًا مجانًا")}</span><Icon name="chevron-right" className="s" /></Link>
          </div>
        </div>
      </section>
      <MktFooter onHome={false} />
    </div>
  );
}
